import React from 'react';
import styled from 'styled-components';
import { TextField } from '@mui/material';
import propTypes from 'prop-types';
import dayjs from 'dayjs';
import { StyledButton } from '../../../../Components/Generics.styled';
import { useAdvertisers } from '../../../../Contexts/AdvertisersContext';

const CampaignBudgetContainer = styled.div`
    margin-top: 100px;
    display: flex;
    height: 400px;
    width: 100%;
    margin-left: auto;
    margin-right: auto;
    flex-direction: column;
`;

const Title = styled.div`
    font-size: 23px;
    font-weight: bold;
    letter-spacing: 0px;
    color: #000000;
    opacity: 1;
    height: 50px;
    flex-direction: column;
    text-align: center;
`;

const BudgetSection = styled.div`
    margin-left: auto;
    margin-right: auto;
    display: flex;
    flex-direction: row;
    justify-content: space-evenly;
`;
const BudgetInfo = styled.div`
    width: 100%;
    height: 100%;
    max-width: 400px;
    background: #ffffff 0% 0% no-repeat padding-box;
    border: 1px solid #cecece;
    border-radius: 11px;
    opacity: 1;
    padding: 32px;
    letter-spacing: var(--unnamed-character-spacing-0);
    text-align: left;
    letter-spacing: 0px;
    opacity: 1;
    margin-left: 20px;
`;
const StyledTextField = styled(TextField)`
    width: 140px;
    top: 30px;
    .MuiOutlinedInput-input {
        background-color: #fafbf7;
    }
`;

const StyledNavButtons = styled.div`
    margin-top: 30px;
    display: flex;
    justify-content: space-between;
    width: 100%;
    max-width: 160px;
    margin-left: auto;
    margin-right: auto;
`;

const CampaignBudget = (props) => {
    const {
        campaignData,
        setCampaignData,
        handleNavButtonNext,
        handleNavButtonBack,
        setPageIndex,
    } = props;
    const { advertiserMetaData } = useAdvertisers();

    const googleMinBudget =
        advertiserMetaData?.selectedAdvertiserPlanData?.googleCampaignMinimumBudget;
    const pontiacMinBudget =
        advertiserMetaData?.selectedAdvertiserPlanData?.pontiacCampaignMinimumBudget;

    const isGoogle = campaignData?.campaignTypeId === 1;
    const isProgrammatic = campaignData?.campaignTypeId === 26;
    const isMobile = campaignData?.campaignTypeId === 2;
    const isVideoAd = campaignData.campaignTypeName === 'video';

    const determineBudget = isGoogle || isMobile ? googleMinBudget : pontiacMinBudget;

    const numberWithCommas = (x) => x?.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');

    const calculateDailyImpressionCount = () => {
        const date1 = new Date(dayjs(campaignData.startDate).format('MM/DD/YYYY'));
        const date2 = new Date(dayjs(campaignData.endDate).format('MM/DD/YYYY'));
        const diffInTime = date2.getTime() - date1.getTime();
        const diffInDays = diffInTime / (1000 * 3600 * 24);

        let impressionCount1;
        let impressionCount2;
        if (isMobile || isGoogle || isProgrammatic) {
            impressionCount1 = ((campaignData.budget / 2.5) * 1000) / diffInDays;
            impressionCount2 = ((campaignData.budget / 5) * 1000) / diffInDays;
        } else if (isVideoAd) {
            impressionCount1 = ((campaignData.budget / 5) * 1000) / diffInDays;
            impressionCount2 = ((campaignData.budget / 10) * 1000) / diffInDays;
        }

        const formattedImpressionCount1 = impressionCount1?.toFixed(0);
        const formattedImpressionCount2 = impressionCount2?.toFixed(0);

        return `${numberWithCommas(formattedImpressionCount2)} - ${numberWithCommas(
            formattedImpressionCount1
        )}`;
    };

    const calculateTotalImpressionCount = () => {
        let impressionCount1;
        let impressionCount2;
        if (isMobile || isGoogle || isProgrammatic) {
            impressionCount1 = (campaignData.budget / 2.5) * 1000;
            impressionCount2 = (campaignData.budget / 5) * 1000;
        } else if (isVideoAd) {
            impressionCount1 = (campaignData.budget / 5) * 1000;
            impressionCount2 = (campaignData.budget / 10) * 1000;
        }

        const formattedImpressionCount1 = impressionCount1.toFixed(0);
        const formattedImpressionCount2 = impressionCount2.toFixed(0);

        return `${numberWithCommas(formattedImpressionCount2)} - ${numberWithCommas(
            formattedImpressionCount1
        )}`;
    };
    const numOnlyRegex = /^[0-9\b]+$/;

    const determineBackRoute = () => {
        if (!isGoogle) {
            setPageIndex(5);
        } else {
            handleNavButtonBack();
        }
    };

    return (
        <CampaignBudgetContainer>
            <Title>Select your Campaign Budget</Title>
            <BudgetSection>
                <StyledTextField
                    id="budget"
                    label="Budget"
                    defaultValue={campaignData.budget}
                    onChange={(e) =>
                        setCampaignData({
                            ...campaignData,
                            budget: numOnlyRegex.test(e.target.value) ? Number(e.target.value) : 0,
                        })
                    }
                    variant="outlined"
                    error={campaignData.budget === '' || campaignData.budget < determineBudget}
                    helperText={
                        campaignData.budget < determineBudget
                            ? `Minimum budget for Davie is $${determineBudget} per month`
                            : ''
                    }
                />
                <BudgetInfo>
                    A typical campaign like this one would generate between
                    <span
                        style={{ paddingLeft: '6px', color: 'red' }}
                    >{`${calculateTotalImpressionCount()} `}</span>
                    total views and between
                    <span
                        style={{ paddingLeft: '6px', color: 'red' }}
                    >{`${calculateDailyImpressionCount()} `}</span>
                    views daily.
                </BudgetInfo>
            </BudgetSection>
            <StyledNavButtons>
                <StyledButton
                    variant="contained"
                    color="primary"
                    onClick={() => handleNavButtonBack()}
                >
                    Back
                </StyledButton>
                <StyledButton
                    variant="contained"
                    color="primary"
                    onClick={() => handleNavButtonNext()}
                    disabled={campaignData.budget === 0 || campaignData.budget < determineBudget}
                >
                    Next
                </StyledButton>
            </StyledNavButtons>
        </CampaignBudgetContainer>
    );
};

CampaignBudget.propTypes = {
    campaignData: propTypes.shape({
        budget: propTypes.number,
        startDate: propTypes.string,
        endDate: propTypes.string,
        campaignTypeName: propTypes.string,
        campaignTypeId: propTypes.number,
    }),
    setCampaignData: propTypes.func,
    handleNavButtonBack: propTypes.func,
    handleNavButtonNext: propTypes.func,
    setPageIndex: propTypes.func,
};

CampaignBudget.defaultProps = {
    campaignData: {},
    setCampaignData: () => {},
    setPageIndex: () => {},
};
export default CampaignBudget;
